import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronDown, Menu, X } from 'lucide-react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { logEventSafe } from '../analytics/analytics';

const Header = () => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  const dropdownTimeoutRef = useRef(null);
  const previousUserIdRef = useRef(null);
  const currentPathRef = useRef('');
  const auth = getAuth();
  const location = useLocation();

  useEffect(() => {
    currentPathRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (currentUser?.uid && previousUserIdRef.current !== currentUser.uid) {
        logEventSafe('login', {
          method: 'firebase_auth',
          page_path: currentPathRef.current,
        });
      }
      previousUserIdRef.current = currentUser?.uid ?? null;
    });
    return unsubscribe;
  }, [auth]);

  useEffect(() => {
    setMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  const navSlideIn = {
    hidden: { y: -100 },
    visible: { y: 0, transition: { type: 'spring', stiffness: 100, damping: 20 } },
  };

  const navLinks = [
    {
      title: 'Product',
      items: [
        { name: 'Features', route: '/features' },
        { name: 'Updates', route: '/updates' },
        { name: 'Teams', route: '/TeamInfo' },
      ],
    },
    {
      title: 'Company',
      items: [
        { name: 'About', route: '/about' },
        { name: 'Contact', route: '/contact' },
        { name: 'Feedback', route: '/feedback' },
      ],
    },
    {
      title: 'Community',
      route: '/community',
    },
    {
      title: 'Pricing',
      route: '/pricing',
    },
  ];

  const handleNavItemClick = (sectionTitle, itemName) => {
    logEventSafe('select_nav_item', {
      nav_section: sectionTitle,
      nav_item: itemName,
      page_path: location.pathname,
    });
  };

  const handleSignInClick = () => {
    logEventSafe('click_sign_in', { page_path: location.pathname });
  };

  const handleAccountClick = () => {
    logEventSafe('click_account', { page_path: location.pathname });
  };

  const handleGetStartedClick = () => {
    logEventSafe('click_get_started', { page_path: location.pathname });
  };

  return (
    <motion.nav
      initial="hidden"
      animate="visible"
      variants={navSlideIn}
      className="fixed inset-x-0 top-0 z-50 border-b border-gray-700/80 bg-gray-800/60 backdrop-blur-md"
    >
      <div className="site-frame">
        <div className="flex min-h-16 items-center justify-between gap-4 py-2">
          <div className="flex min-w-0 items-center gap-4 xl:gap-8">
            <Link to="/" className="truncate text-xl font-bold animate-gradient-text bg-clip-text text-transparent sm:text-2xl unselectable">
              Toolip
            </Link>

            <div className="hidden lg:flex items-center space-x-1 xl:space-x-3">
              {navLinks.map((section) => {
                if (section.items) {
                  return (
                    <div
                      key={section.title}
                      className="relative"
                      onMouseEnter={() => {
                        if (dropdownTimeoutRef.current) {
                          clearTimeout(dropdownTimeoutRef.current);
                          dropdownTimeoutRef.current = null;
                        }
                        setActiveDropdown(section.title);
                      }}
                      onMouseLeave={() => {
                        dropdownTimeoutRef.current = setTimeout(() => setActiveDropdown(null), 150);
                      }}
                    >
                      <button className="inline-flex items-center rounded-md px-3 py-2 text-gray-300 transition-colors hover:bg-white/5 hover:text-gray-100">
                        {section.title}
                        <motion.span
                          className="ml-1 inline-flex h-4 w-4 shrink-0 items-center justify-center origin-center"
                          animate={{ rotate: activeDropdown === section.title ? 180 : 0 }}
                          transition={{ duration: 0.2 }}
                          style={{ transformOrigin: '50% 50%' }}
                        >
                          <ChevronDown className="h-4 w-4" />
                        </motion.span>
                      </button>
                      <motion.div
                        className="absolute left-0 mt-2 w-56 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl bg-gray-900/95 shadow-lg ring-1 ring-white/10"
                        initial="hidden"
                        animate={activeDropdown === section.title ? 'visible' : 'hidden'}
                        variants={{
                          hidden: { opacity: 0, y: -5, pointerEvents: 'none' },
                          visible: { opacity: 1, y: 0, pointerEvents: 'auto' },
                        }}
                        transition={{ duration: 0.18 }}
                      >
                        <div className="py-2">
                          {section.items.map((item) => (
                            <Link
                              key={item.name}
                              to={item.route}
                              className="block px-4 py-3 text-sm text-gray-300 hover:bg-white/5 hover:text-white"
                              onClick={() => handleNavItemClick(section.title, item.name)}
                            >
                              {item.name}
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    </div>
                  );
                }

                return (
                  <Link
                    key={section.title}
                    to={section.route}
                    className="rounded-md px-3 py-2 text-gray-300 transition-colors hover:bg-white/5 hover:text-gray-100"
                    onClick={() => handleNavItemClick('top', section.title)}
                  >
                    {section.title}
                  </Link>
                );
              })}
            </div>
          </div>

          <div className="hidden lg:flex items-center space-x-2 xl:space-x-4">
            {user ? (
              <Link to="/account" className="px-3 py-2 text-gray-300 hover:text-gray-100" onClick={handleAccountClick}>
                Account
              </Link>
            ) : (
              <Link to="/LoginPage" className="px-3 py-2 text-gray-300 hover:text-gray-100" onClick={handleSignInClick}>
                Sign In
              </Link>
            )}
            <Link
              to="/get-started"
              className="rounded-md bg-blue-600 px-4 py-2 text-white transition-colors duration-200 hover:bg-blue-700"
              onClick={handleGetStartedClick}
            >
              Download
            </Link>
          </div>

          <button
            type="button"
            className="inline-flex items-center justify-center rounded-md p-2 text-gray-300 hover:bg-white/5 hover:text-white lg:hidden"
            onClick={() => setMobileMenuOpen((open) => !open)}
            aria-label="Toggle navigation"
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="border-t border-gray-700/80 pb-4 lg:hidden">
            <div className="space-y-4 pt-4">
              {navLinks.map((section) => (
                <div key={section.title}>
                  {section.items ? (
                    <div className="space-y-2">
                      <div className="px-1 text-xs uppercase tracking-[0.25em] text-gray-500">{section.title}</div>
                      <div className="grid gap-2">
                        {section.items.map((item) => (
                          <Link
                            key={item.name}
                            to={item.route}
                            className="block rounded-lg bg-white/5 px-3 py-2 text-gray-200 hover:bg-white/10"
                            onClick={() => handleNavItemClick(section.title, item.name)}
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <Link
                      to={section.route}
                      className="block rounded-lg bg-white/5 px-3 py-2 text-gray-200 hover:bg-white/10"
                      onClick={() => handleNavItemClick('top', section.title)}
                    >
                      {section.title}
                    </Link>
                  )}
                </div>
              ))}

              <div className="grid grid-cols-1 gap-2 pt-2 sm:grid-cols-2">
                {user ? (
                  <Link
                    to="/account"
                    className="block rounded-lg bg-white/5 px-3 py-2 text-center text-gray-200 hover:bg-white/10"
                    onClick={handleAccountClick}
                  >
                    Account
                  </Link>
                ) : (
                  <Link
                    to="/LoginPage"
                    className="block rounded-lg bg-white/5 px-3 py-2 text-center text-gray-200 hover:bg-white/10"
                    onClick={handleSignInClick}
                  >
                    Sign In
                  </Link>
                )}
                <Link
                  to="/get-started"
                  className="block rounded-lg bg-blue-600 px-3 py-2 text-center text-white hover:bg-blue-700"
                  onClick={handleGetStartedClick}
                >
                  Download
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </motion.nav>
  );
};

export default Header;
